import React from "react";
import { TextField, Autocomplete, Chip, Box } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { connect } from "react-redux";
import { addSearchFilter, removeSearchFilter, clearSearchFilters } from "../../../store/job/jobSlice";

const Search = ({
  search_columns = {},
  searchFilters,
  addSearchFilter,
  removeSearchFilter,
  clearSearchFilters,
  onSearchSubmit,
}) => {
  // Transform search_columns into options
  // Handles both array (['col1', 'col2']) and object ({ col1: 'Label1' }) formats
  const columnOptions = React.useMemo(() => {
    if (Array.isArray(search_columns)) {
      return search_columns.map(col => {
        if (typeof col === 'string') {
          return {
            key: col,
            label: col.replace(/_/g, ' ')
          };
        }
        return {
          key: col.key,
          label: col.label || col.key
        };
      });
    }
    return Object.entries(search_columns).map(([key, label]) => ({
      key,
      label,
    }));
  }, [search_columns]);

  const [inputValue, setInputValue] = React.useState("");

  const handleAddFilter = (column, value) => {
    if (!value?.trim()) return;

    const filter = {
      value: value.trim(),
      key: column?.key || null, // Use null for global/keyless search
      label: column?.label || "Search",
    };

    addSearchFilter(filter);
    setInputValue("");
    if (onSearchSubmit) onSearchSubmit();
  };

  const handleDelete = (filter) => {
    removeSearchFilter(filter);
  };

  const filterColumnOptions = (options) => options;

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        multiple
        freeSolo
        options={columnOptions}
        filterOptions={filterColumnOptions}
        value={searchFilters}
        inputValue={inputValue}
        openOnFocus={true} // Allow dropdown to open on click/focus
        forcePopupIcon={false} // Hide the down arrow icon
        onInputChange={(e, newInput) => {
          setInputValue(newInput);
        }}
        onChange={(e, newValue, reason, details) => {
          if (reason === "selectOption" && details?.option) {
            handleAddFilter(details.option, inputValue);
            setInputValue("");
          } else if (reason === "removeOption") {
          } else if (reason === "clear") {
            clearSearchFilters();
            setInputValue("");
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && inputValue.trim()) {
            e.preventDefault();
            handleAddFilter(null, inputValue);
            setInputValue("");
          }
        }}
        getOptionLabel={(option) => option.label || ""}
        renderTags={(value, getTagProps) =>
          value.map((filter, index) => (
            <Chip
              {...getTagProps({ index })}
              key={`${filter.key}-${filter.value}`}
              label={
                filter.key && filter.label
                  ? `${filter.label}: ${filter.value}`
                  : filter.value
              }
              onDelete={() => handleDelete(filter)}
            />
          ))
        }
        renderInput={(params) => (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <TextField
              {...params}
              placeholder="Search..."
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: null, // We are placing the icon manually
                sx: {
                  fontSize: '0.95rem',
                  color: '#666',
                  paddingLeft: '10px',
                  // Ensure chips don't overlap with the search icon
                  paddingRight: '40px',
                  '&::placeholder': {
                    color: '#999',
                    opacity: 1
                  }
                }
              }}
              sx={{
                '& .MuiInput-root': {
                  borderBottom: '2px solid #b6b4b4ff',
                  paddingBottom: '8px',
                  borderRadius: '5px',
                  '&:hover': {
                    borderBottom: '2px solid #ccc',
                  },
                  '&.Mui-focused': {
                    borderBottom: '2px solid black',
                  }
                }
              }}
            />
            <SearchIcon
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'black',
                cursor: 'pointer',
                fontSize: '30px',
                zIndex: 1
              }}
              onClick={() => {
                if (inputValue.trim()) {
                  handleAddFilter(null, inputValue);
                  setInputValue("");
                }
              }}
            />
          </Box>
        )}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  searchFilters: state.job.searchFilters,
  search_columns: state.job.search_columns,
});

const mapDispatchToProps = {
  addSearchFilter,
  removeSearchFilter,
  clearSearchFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(Search);